<!-- Menú de una conexión (maps[index]): regla del campo, orden de preferencia y acciones con subvistas
     (PickList). Solo emite: el padre aplica cada acción con deshacer. Esc por capas: desplegable → subvista →
     menú; Supr quita la conexión; Intro (fuera de botones) abre el campo. -->
<template>
  <div ref="rootRef" class="em" role="dialog" :aria-labelledby="titleId" :style="boxStyle" @keydown="onKey">
    <template v-if="view === 'main'">
      <div class="em-head">
        <h3 :id="titleId" ref="titleRef" class="em-title" tabindex="-1">
          «{{ label }}» <span class="em-arrow" aria-hidden="true">→</span> <span class="mono">{{ field.name }}</span>
        </h3>
        <button type="button" class="em-x" :aria-label="t('ed.close')" :title="t('ed.close')" @click="emit('close')">✕</button>
      </div>
      <div class="em-sub">
        <span class="mono">{{ alias }}</span> · {{ pos.n > 1 ? t('em.position', { i: pos.i, n: pos.n }) : t('em.only') }}
      </div>
      <div v-if="carried" class="em-carried">
        {{ t('em.carried', { k: carried.k, N: carried.N }) }}
      </div>
      <ul v-if="carried && carried.forms && carried.forms.length <= DOTS_MAX" class="em-forms">
        <li v-for="(f, i) in carried.forms" :key="i" :class="{ off: !f.on }" :title="`P${f.prio} ${f.name}: ${t(f.on ? 'em.formOn' : 'em.formOff')}`">
          <span class="fbadge" :style="f.on ? { '--fc': f.color } : null">{{ f.code }}</span><span class="em-fname">{{ f.name }}</span>
        </li>
      </ul>
      <p v-if="carried" class="em-global">{{ t('em.global') }}</p>

      <div class="em-sec">
        <div class="em-cap">{{ t('em.rule') }} · {{ pos.n > 1 ? t('em.ruleAll', { n: pos.n }) : t('em.ruleOne') }}</div>
        <div class="em-segs">
          <div class="seg" role="group" :aria-label="t('em.rule')">
            <button v-for="m in MERGES" :key="m" type="button" :class="{ on: rule.mergeMode === m }" :aria-pressed="rule.mergeMode === m"
                    :title="t('merge.' + m + '.d')" @click="emit('rule', { mergeMode: m })">{{ t('seg.' + m) }}</button>
          </div>
          <div class="seg" role="group" :aria-label="t('em.missing')">
            <button v-for="o in MISSING" :key="o" type="button" :class="{ on: rule.onMissing === o }" :aria-pressed="rule.onMissing === o"
                    :title="t('missing.' + o + '.d')" @click="emit('rule', { onMissing: o })">{{ t('seg.' + o) }}</button>
          </div>
        </div>
        <p v-if="line" class="em-line">{{ line }}</p>
      </div>

      <div class="em-sec em-acts">
        <template v-if="pos.n > 1">
          <button type="button" class="em-act" :disabled="pos.i <= 1" @click="emit('up')"><span aria-hidden="true">↑</span> {{ t('em.up') }}</button>
          <button type="button" class="em-act" :disabled="pos.i >= pos.n" @click="emit('down')"><span aria-hidden="true">↓</span> {{ t('em.down') }}</button>
          <button v-if="pos.i > 1" type="button" class="em-act" @click="emit('first')"><span aria-hidden="true">⇤</span> {{ t('em.first') }}</button>
        </template>
        <button type="button" class="em-act" data-act="replace" @click="go('replace')"><span aria-hidden="true">⇄</span> {{ t('em.replace') }}</button>
        <button type="button" class="em-act" data-act="move" @click="go('move')"><span aria-hidden="true">→</span> {{ t('em.move') }}</button>
        <button type="button" class="em-act" data-act="copy" @click="go('copy')"><span aria-hidden="true">＋</span> {{ t('em.copy') }}</button>
        <button type="button" class="em-act" data-act="fallback" @click="go('fallback')"><span aria-hidden="true">＋</span> {{ t('em.fallback') }}</button>
        <button v-if="carried" type="button" class="em-act wide" data-act="forms" @click="go('forms')"><span aria-hidden="true">☰</span> {{ t('em.forms') }}</button>
      </div>

      <div class="em-foot">
        <button type="button" class="em-act danger" @click="emit('remove')">
          <span aria-hidden="true">⌫</span> {{ t('em.remove') }} <kbd>{{ t('em.removeKey') }}</kbd>
        </button>
        <span class="grow" />
        <button type="button" class="btn primary em-open" @click="emit('open')">{{ t('em.open') }}</button>
      </div>
    </template>

    <template v-else>
      <div class="em-head">
        <button type="button" class="em-back" @click="back">← {{ t('em.back') }}</button>
        <h3 :id="titleId" ref="titleRef" class="em-title" tabindex="-1">{{ subTitle }}</h3>
      </div>
      <ul v-if="view === 'forms'" class="em-forms">
        <li v-for="(f, i) in carried.forms" :key="i" :class="{ off: !f.on }">
          <span class="mono em-prio">P{{ f.prio }}</span>
          <span class="em-fname">{{ f.name }}</span>
          <span class="em-fstate">{{ f.on ? t('em.formOn') : t('em.formOff') }}</span>
        </li>
      </ul>
      <PickList v-else :options="view === 'move' || view === 'copy' ? fieldOptions : aliasOptions" autofocus
                @pick="onPick" @close="back" />
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import PickList from '@vem/components/PickList.vue';
import { t } from '@vem/i18n.js';

let seq = 0;
const MERGES = ['last', 'first', 'priority', 'concat'];
const MISSING = ['keep', 'clear'];
const DOTS_MAX = 8;
const W = 400;
const MARGIN = 8;
const GAP = 12;

const props = defineProps({
  /** Ancla (el asa de la línea), en px relativos a .canvas-box. */
  x: { type: Number, required: true },
  y: { type: Number, required: true },
  /** Tamaño de .canvas-box: el menú se recoloca para no salirse. */
  bounds: { type: Object, required: true },
  /** Índice de la conexión en dto.maps. */
  index: { type: Number, required: true },
  alias: { type: String, required: true },
  /** Etiqueta de la pregunta (aliasLabel o sysShort). */
  label: { type: String, required: true },
  /** Campo destino: {id: 'Clase#campo', name, cls}. */
  field: { type: Object, required: true },
  /** Posición en la cadena del campo, 1-based: {i, n}. */
  pos: { type: Object, required: true },
  /** Regla del campo: {mergeMode, onMissing}. */
  rule: { type: Object, required: true },
  /** Resumen de una línea de la receta del campo. */
  line: { type: String, default: '' },
  /** Cobertura {k, N, forms: [{name, prio, on}]}; null en un dato de la encuesta. */
  carried: { type: Object, default: null },
  /** Opciones PickList de preguntas (cambiar / añadir detrás). */
  aliasOptions: { type: Array, default: () => [] },
  /** Opciones PickList de campos (mover / conectar también), value = 'Clase#campo'. */
  fieldOptions: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'rule', 'up', 'down', 'first', 'replace', 'move', 'copy', 'fallback', 'forms', 'open', 'remove']);

const titleId = `em-title-${++seq}`;
const rootRef = ref(null);
const titleRef = ref(null);
const view = ref('main');
const h = ref(0);
let formsOn = false;

const subTitle = computed(() => {
  const q = `«${props.label}»`;
  if (view.value === 'move') return t('em.moveTitle', { q });
  if (view.value === 'forms') return t('em.carried', { k: props.carried?.k ?? 0, N: props.carried?.N ?? 0 });
  return `${q} · ${t('em.' + view.value)}`;
});

/** Debajo del ancla si cabe; si no, encima; siempre dentro de bounds. */
const boxStyle = computed(() => {
  const bw = props.bounds.w || W;
  const bh = props.bounds.h || 0;
  const w = Math.min(W, Math.max(0, bw - 2 * MARGIN));
  const left = Math.max(MARGIN, Math.min(props.x - w / 2, bw - w - MARGIN));
  let top = props.y + GAP;
  if (bh && top + h.value > bh - MARGIN) top = props.y - GAP - h.value;
  if (bh) top = Math.max(MARGIN, Math.min(top, bh - h.value - MARGIN));
  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(Math.max(MARGIN, top))}px`,
    width: `${w}px`,
    maxHeight: bh ? `${bh - 2 * MARGIN}px` : null,
  };
});

function measure() {
  nextTick(() => {
    if (rootRef.value) h.value = rootRef.value.offsetHeight;
  });
}

let from = null;

function go(v) {
  from = v;
  view.value = v;
  if (v === 'forms' && !formsOn) {
    formsOn = true;
    emit('forms', true);
  }
  if (v === 'forms') nextTick(() => titleRef.value?.focus());
}

function back() {
  view.value = 'main';
  nextTick(() => (rootRef.value?.querySelector(`[data-act="${from}"]`) || titleRef.value)?.focus());
}

function onPick(value) {
  const v = view.value;
  if (v === 'replace') emit('replace', value);
  else if (v === 'fallback') emit('fallback', value);
  else if (v === 'move') emit('move', value);
  else if (v === 'copy') emit('copy', value);
}

const inField = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');

function onKey(e) {
  if (e.key === 'Escape') {
    if (view.value !== 'main') back();
    else emit('close');
  } else if (e.key === 'Delete' && view.value === 'main' && !inField(e.target)) {
    emit('remove');
  } else if (e.key === 'Enter' && view.value === 'main' && e.target === titleRef.value) {
    emit('open');
  } else {
    return;
  }
  e.preventDefault();
  e.stopPropagation();
}

function onDocDown(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) emit('close');
}

watch(view, measure);
watch(() => [props.index, props.pos.n, props.line, props.carried], measure);
// Otra conexión en el mismo menú: vuelve a la vista principal.
watch(() => props.index, () => {
  view.value = 'main';
});

onMounted(() => {
  measure();
  nextTick(() => titleRef.value?.focus({ preventScroll: true }));
  document.addEventListener('pointerdown', onDocDown);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown);
  if (formsOn) emit('forms', false);
});
</script>

<style>
.vem-root .em {
  position: absolute; z-index: 25; overflow-y: auto; background: var(--panel); border: 1px solid var(--line2);
  border-top: 3px solid var(--teal); border-radius: 12px; padding: 10px 12px 12px; box-shadow: 0 14px 36px var(--shadow);
  font-size: 12.5px;
}
.vem-root .em-head { display: flex; align-items: flex-start; gap: 8px; }
.vem-root .em-title { flex: 1; min-width: 0; margin: 0; font-size: 13.5px; font-weight: 600; line-height: 1.35; word-break: normal; overflow-wrap: anywhere; }
.vem-root .em-title:focus { outline: none; }
.vem-root .em-title:focus-visible { outline: 2px solid var(--mint); outline-offset: 2px; border-radius: 4px; }
.vem-root .em-arrow { color: var(--teal); }
.vem-root .em-title .mono { color: var(--lime); font-size: 12.5px; }
.vem-root .em-x { background: none; border: 0; color: var(--tx2); cursor: pointer; padding: 0 2px; font-size: 14px; line-height: 1; }
.vem-root .em-back { background: none; border: 0; padding: 1px 0; color: var(--mint); font-weight: 600; cursor: pointer; white-space: nowrap; }
.vem-root .em-sub { margin-top: 3px; color: var(--tx2); font-size: 11.5px; overflow-wrap: anywhere; }
.vem-root .em-sub .mono { font-size: 10.5px; color: var(--tx3); }
.vem-root .em-carried { display: flex; align-items: center; gap: 8px; margin-top: 4px; color: var(--tx2); }
.vem-root .em-forms { list-style: none; margin: 6px 0 0; padding: 0; display: grid; gap: 3px; }
.vem-root .em-forms li { display: flex; align-items: center; gap: 6px; font-size: 12px; min-width: 0; }
.vem-root .em-forms li.off { color: var(--tx3); text-decoration: line-through; }
.vem-root .em-forms li.off .fbadge { background: none; color: var(--tx3); box-shadow: inset 0 0 0 1px var(--line2); }
.vem-root .em-fname { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vem-root .em-global { margin: 3px 0 0; color: var(--tx3); font-size: 11.5px; }
.vem-root .em-sec { border-top: 1px solid var(--line2); margin-top: 10px; padding-top: 9px; }
.vem-root .em-cap { font-family: var(--mono); font-size: 9.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--tx3); margin-bottom: 6px; }
.vem-root .em-segs { display: flex; gap: 8px; flex-wrap: wrap; }
.vem-root .em-segs .seg button { padding: 4px 8px; min-height: 26px; font-size: 12px; }
.vem-root .em-line { margin: 6px 0 0; color: var(--tx2); font-size: 11.5px; line-height: 1.4; }
.vem-root .em-acts { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; }
.vem-root .em-act {
  display: flex; align-items: center; gap: 6px; background: none; border: 0; border-radius: 7px; padding: 5px 6px;
  color: var(--tx); cursor: pointer; text-align: left; font-size: 12.5px;
}
.vem-root .em-act > span[aria-hidden] { flex: 0 0 14px; color: var(--mint); text-align: center; }
.vem-root .em-act:hover:not(:disabled) { background: var(--hover); }
.vem-root .em-act:disabled { color: var(--tx3); cursor: default; }
.vem-root .em-act:disabled > span[aria-hidden] { color: var(--tx3); }
.vem-root .em-act.wide { grid-column: 1 / -1; }
.vem-root .em-act.danger, .vem-root .em-act.danger > span[aria-hidden] { color: var(--danger); }
.vem-root .em-act kbd { font-family: var(--mono); font-size: 10px; color: var(--tx3); border: 1px solid var(--line2); border-radius: 4px; padding: 0 4px; }
.vem-root .em-foot { display: flex; align-items: center; gap: 8px; border-top: 1px solid var(--line2); margin-top: 10px; padding-top: 9px; }
.vem-root .em-open { min-height: 32px; padding: 5px 12px; }
.vem-root .em-head + .pk { margin-top: 10px; }
.vem-root .em-forms { list-style: none; margin: 10px 0 0; padding: 0; }
.vem-root .em-forms li { display: flex; align-items: baseline; gap: 8px; padding: 4px 2px; border-top: 1px solid var(--line); }
.vem-root .em-forms li:first-child { border-top: 0; }
.vem-root .em-prio { flex: 0 0 auto; font-size: 10.5px; font-weight: 600; color: var(--cyan); }
.vem-root .em-fname { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.vem-root .em-fstate { flex: 0 0 auto; font-size: 11.5px; color: var(--mint); }
.vem-root .em-forms li.off .em-fstate { color: var(--tx3); }
@media (max-width: 760px) { .vem-root .em-acts { grid-template-columns: 1fr; } }
</style>
